import { subscribeToSSE } from "../eventsClient/sse";
import { getBackendURL } from "../getBaseUrl";
import { type Notification, NotificationStreamPayloadSchema } from "./notificationsClient.types";

export interface NotificationSubscriptionOptions {
  authToken: () => string | undefined;
  onNotification: (notification: Notification) => void;
  onError?: (error: Error) => void;
  /** Fired once a connection after a failed or dropped attempt proves healthy, not on every 200. */
  onReconnect?: () => void;
}

export interface NotificationSubscription {
  close: () => void;
}

// Per-user notification inbox stream. Unlike the project events stream this is
// scoped only by the bearer token (no X-Project-Id) and resumes from the last
// seen Redis Stream id via Last-Event-ID so a reconnect replays only the gap.
//
// No attempt ceiling: the server fails a tail within a second whenever its
// blocking Redis pool is full, so a ceiling of a few attempts turns a minute of
// load into a bell that stays dead until reload. The capped backoff keeps a
// down backend at about one request per tab every 30s.
export function subscribeToNotifications(options: NotificationSubscriptionOptions): NotificationSubscription {
  return subscribeToSSE({
    url: `${getBackendURL()}/notifications/stream`,
    buildHeaders: () => {
      const headers: Record<string, string> = {};
      const token = options.authToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      return headers;
    },
    onData: (data) => {
      try {
        const result = NotificationStreamPayloadSchema.safeParse(JSON.parse(atob(data)));
        if (!result.success) {
          options.onError?.(new Error(`Failed to parse notification: ${result.error.message}`));
          return;
        }
        options.onNotification(result.data.notification);
      } catch (error) {
        options.onError?.(error instanceof Error ? error : new Error("Failed to process notification"));
      }
    },
    onError: options.onError,
    onReconnect: options.onReconnect,
  });
}
